import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
    Container, Row, Col, Card, Table, Button, Form, InputGroup, Badge, Dropdown, Spinner
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import Utility from "../../../../GlobalModules/Utility/Utility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDownload,
    faCheck,
    faEllipsisV,
    faEye,
    faPlus,
    faSearch,
    faSort,
    faTrash, faEdit,
    faSync
} from "@fortawesome/free-solid-svg-icons";
import { DebounceInput } from "react-debounce-input";
import { Link, useHistory, useParams } from "react-router-dom";
import Pagination from "../../../../GlobalModules/Utility/Components/Pagination";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import { deleteAccount, getAccountList } from "../Controller/AccountController";

// tiny portal component that forwards the ref React-Bootstrap needs
const MenuPortal = React.forwardRef(({ children, ...props }, ref) => {
    return ReactDOM.createPortal(
        <div ref={ref} {...props}>{children}</div>,
        document.body
    );
});

const AccountList = (props) => {
    const { t, i18n } = props;
    const lng = i18n.language;
    const { control, watch } = useForm({ defaultValues: { query: "" }, mode: 'onChange' });
    const { query } = watch();

    // const { lng } = useParams();
    const history = useHistory();
    const [accounts, setAccounts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('id');
    const [sortDirection, setSortDirection] = useState('desc');
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState({
        limit: 10,
        offset: 0,
        sort: [{ field: 'id', sort: 'desc' }],
        search: ''
    });

    // open dropdown id (controlled)
    const [openId, setOpenId] = useState(null);

    const handleLimitChange = (limit) => {
        const newLimit = parseInt(limit, 10);
        setItemsPerPage(newLimit);
        setFilters(prev => {
            if (prev.limit === newLimit && prev.offset === 0) return prev;
            return { ...prev, limit: newLimit, offset: 0 };
        });
        setCurrentPage(1);
    };

    // fetch list (memoized)
    const fetchAccounts = useCallback(async () => {
        try {
            setLoading(true);
            if (props.userEntryType.user_type == "staff") {
                if (localStorage.getItem('store_id') == null) {
                    localStorage.setItem('store_id', props.userEntryType.branch_id)
                }
            }
            const response = await getAccountList(filters);
            if (response.success) {
                setAccounts(response.data);
                setTotalItems(response.total);
            } else {
                toast.error(response.message || t('accountList.fetchFailed'));
                setAccounts([]);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            toast.error(t('accountList.fetchError'));
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }, [filters, props.userEntryType]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    const handleDelete = async (slack) => {
        if (window.confirm(t('accountList.deleteConfirmation'))) {
            try {
                const response = await deleteAccount(slack);
                if (response.success) {
                    // toast.success(t('accountList.deleteSuccess'));
                    Utility.toastNotifications(
                        response.message || t('accountList.deleteSuccess'),
                        'Success',
                        'success'
                    )
                    fetchAccounts(); // Refresh the list
                } else {
                    // toast.error(response.message || t('accountList.deleteFailed'));
                    Utility.toastNotifications(
                        response.message || t('accountList.deleteFailed'),
                        'Error',
                        'error'
                    )
                }
            } catch (error) {
                console.error('Error deleting account:', error);
                // toast.error(t('accountList.deleteError'));
            }
        }
    };
    const handleRefresh = () => {
        fetchAccounts()
    }
    const handlePageChange = (page) => {
        setCurrentPage(page);
        setFilters(prev => {
            const nextOffset = (page - 1) * prev.limit;
            if (prev.offset === nextOffset) return prev;
            return { ...prev, offset: nextOffset };
        });
    };

    const handleSort = (field) => {
        const newDirection = sortField === field
            ? (sortDirection === 'asc' ? 'desc' : 'asc')
            : 'asc';

        setSortField(field);
        setSortDirection(newDirection);

        setFilters(prev => ({
            ...prev,
            sort: [{ field, sort: newDirection }]
        }));
    };

    //======= add action permission ====//
    // Check multiple permissions at once
    const accountsPermissions = Utility.hasMultipleActionPermissions(
        'business_account',
        'accounts',
        ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'generate_invoice', 'change_status', 'accept', 'reject', 'KOT info', 'cancel', 'bill', 'bill_post', 'cancel_bill_post', 'invoice'],
        props.userCredentials.user_details.user_type || ['admin', 'app_admin']
    );

    // Check if any actions are available for display in the table
    const hamburgerTableActions = accountsPermissions.view || accountsPermissions.edit || accountsPermissions.delete || accountsPermissions.bill || accountsPermissions.invoice;

    return (
        <Container fluid className="p-0">
            <div className="d-flex justify-content-between align-items-center top-header-box">
                <h4>{t('accountList.title')}</h4>
                {(props.userCredentials?.user_details?.user_type === "admin" ||
                    (Object.keys(Utility.getRoleObjectByEventKey('supplier')).length > 0 && (Utility.getRoleObjectByEventKey('supplier').subeventKey.length > 0 && Utility.getRoleObjectByEventKey('supplier').subeventKey[0].eventKey == "suppliers" && (Utility.getRoleObjectByEventKey('supplier').subeventKey[0].rules == "CRUD" || Utility.getRoleObjectByEventKey('supplier').subeventKey[0].rules == "CUD")))) && (
                        <div>
                            {accountsPermissions.create && (
                                <Button
                                    variant="primary"
                                    onClick={() => history.push(`/${lng}/business/accounts/add`)}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-2" /> {t('accountList.newAccount')}
                                </Button>
                            )}
                        </div>
                    )}
            </div>

            <Card className="custom-border-light">
                <Card.Body>
                    <Form noValidate>
                        <Row className="mb-0 d-flex justify-content-end">
                            <Col md={3}>
                                <InputGroup className="mb-3 input-group input-group-sm">
                                    <InputGroup.Text>
                                        <FontAwesomeIcon icon={faSearch} />
                                    </InputGroup.Text>

                                    <Controller
                                        name="query"
                                        control={control}
                                        rules={{ minLength: 2 }}
                                        render={({ field }) => (
                                            <DebounceInput
                                                {...field}
                                                minLength={2}
                                                debounceTimeout={500}
                                                className="form-control"
                                                // placeholder="Search accounts..."
                                                placeholder={t('accountList.searchPlaceholder')}
                                                onKeyDown={(e) => {
                                                    if (e.key === " ") e.preventDefault();
                                                }}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    field.onChange(val);
                                                    setFilters(prev => {
                                                        const nextSearch = val || "";
                                                        if (prev.search === nextSearch && prev.offset === 0) return prev; // avoid no-op updates
                                                        return { ...prev, search: nextSearch, offset: 0 };
                                                    });
                                                    setCurrentPage(1);
                                                }}
                                            />
                                        )}
                                    />
                                    <div className='ms-2'>
                                        <Button
                                            variant='outline-secondary'
                                            size='sm'
                                            onClick={handleRefresh}
                                            disabled={loading}
                                            title='Refresh'
                                        >
                                            <FontAwesomeIcon
                                                icon={faSync}
                                                spin={loading}
                                            />
                                        </Button>
                                    </div>
                                </InputGroup>
                            </Col>
                            {/* <Col md={8}></Col> */}
                        </Row>
                    </Form>

                    <div className="table-responsive">
                        <Table hover className="align-middle">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('stock_transfer_reference')} style={{ cursor: 'pointer' }}>
                                        {t('accountList.tableHeaders.accountCode')} {sortField === 'stock_transfer_reference' && <FontAwesomeIcon icon={faSort} size="xs" />}
                                    </th>
                                    <th onClick={() => handleSort('from_store_code')} style={{ cursor: 'pointer' }}>
                                        {t('accountList.tableHeaders.accountName')} {sortField === 'from_store_code' && <FontAwesomeIcon icon={faSort} size="xs" />}
                                    </th>
                                    <th onClick={() => handleSort('from_store_name')} style={{ cursor: 'pointer' }}>
                                        {t('accountList.tableHeaders.accountType')} {sortField === 'from_store_name' && <FontAwesomeIcon icon={faSort} size="xs" />}
                                    </th>
                                    <th onClick={() => handleSort('to_store_code')} style={{ cursor: 'pointer' }}>
                                        {t('accountList.tableHeaders.posDefaultAccount')} {sortField === 'to_store_code' && <FontAwesomeIcon icon={faSort} size="xs" />}
                                    </th>
                                    <th onClick={() => handleSort('to_store_name')} style={{ cursor: 'pointer' }}>
                                        {t('accountList.tableHeaders.status')} {sortField === 'to_store_name' && <FontAwesomeIcon icon={faSort} size="xs" />}
                                    </th>
                                    <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                                        {t('accountList.tableHeaders.createdOn')} {sortField === 'createdAt' && <FontAwesomeIcon icon={faSort} size="xs" />}
                                    </th>
                                    {/* <th onClick={() => handleSort('updatedAt')} style={{ cursor: 'pointer' }}>
                                        {t('accountList.tableHeaders.updatedOn')} {sortField === 'updatedAt' && <FontAwesomeIcon icon={faSort} size="xs" />}
                                    </th> */}
                                    {hamburgerTableActions && (
                                        <th>{t('accountList.tableHeaders.actions')}</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={hamburgerTableActions ? 8 : 7} className="text-center py-4">
                                            {/* <Spinner animation="border" variant="primary"/>
                                        <span className="ms-2">{t('accountList.loading')}</span> */}
                                        </td>
                                    </tr>
                                ) : accounts.length > 0 ? (
                                    accounts.map(account => (
                                        <tr key={account.id}>
                                            <td>{account.account_code}</td>
                                            <td>{account.label}</td>
                                            <td>{account.account_type}</td>
                                            <td>{account.pos_default_account
                                                === 1 ? t('accountList.yes') : t('accountList.no')}</td>
                                            <td>
                                                <Badge
                                                    bg={account.status.label === t('accountList.active') ? 'success' : 'secondary'}
                                                // style={{ cursor: 'pointer' }}
                                                >
                                                    {account.status.label || t('accountList.inactive')}
                                                </Badge>
                                            </td>
                                            <td>{Utility.get12HourFormat(account.createdAt) || '-'}</td>
                                            {/* <td>{Utility.get12HourFormat(account.updatedAt) || '-'}</td> */}
                                            {hamburgerTableActions && (
                                                <td style={{ position: 'static' }}>
                                                    <Dropdown
                                                        align="end"
                                                        show={openId === account.id}
                                                        onToggle={(next) => setOpenId(next ? account.id : null)}
                                                    >
                                                        <Dropdown.Toggle
                                                            variant="light"
                                                            size="sm"
                                                            id={`dropdown-${account.id}`}
                                                            className="btn-icon no-caret"
                                                            onClick={() =>
                                                                setOpenId(prev => (prev === account.id ? null : account.id))
                                                            }
                                                        >
                                                            <FontAwesomeIcon icon={faEllipsisV} />
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu renderOnMount as={MenuPortal}>
                                                            {/* {accountsPermissions.view && ( */}
                                                            <Dropdown.Item as={Link} to={`/${lng}/business/accounts/view/${account.slack}`}>
                                                                <FontAwesomeIcon icon={faEye} className="me-2" /> View
                                                            </Dropdown.Item>
                                                            {/* )} */}
                                                            {accountsPermissions.edit && (
                                                                <Dropdown.Item as={Link} to={`/${lng}/business/accounts/edit/${account.slack}`}>
                                                                    <FontAwesomeIcon icon={faEdit} className="me-2" /> Edit
                                                                </Dropdown.Item>
                                                            )}
                                                            {accountsPermissions.delete && (<>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item className="text-danger" onClick={() => handleDelete(account.slack)}>
                                                                    <FontAwesomeIcon icon={faTrash} className="me-2" /> Delete
                                                                </Dropdown.Item>
                                                            </>)}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={hamburgerTableActions ? 13 : 12} className="text-center py-4 nosearchResult">
                                            {query ? t('accountList.noSearchResults') : t('accountList.noAccountsFound')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <Pagination
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        siblingCount={2}
                        onLimitChange={handleLimitChange}
                        limitOptions={[10, 25, 50, 100, 500, 1000]}
                    />
                </Card.Body>
            </Card>
        </Container>
    );
};

const mapStateToProps = (globalState) => ({
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType
});

export default connect(mapStateToProps)(withNamespaces()(AccountList));
